import React, { FC, ChangeEvent, useRef, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';

import Yup from 'utils/yup';
import { Field, Form, Formik, FormikConfig, FormikProps } from 'formik';
import { Divider, FormControlLabel, FormHelperText, Grid, Radio, RadioGroup } from '@mui/material';

import { BaseDialogProps, Dialog, LeoDateInterval, showNotification } from 'components';
import { NewPerformacePayModel, NotificationType } from 'models';
import { useWorkerListSearch } from 'features/workers';
import {
  performanceBasedPayKeys,
  useCreatePerformanceBasedPay,
  useDefaultPerformanceBasedPayInterval,
  useGetActivePerformanceBasedPayJob,
  usePollPerformanceBasedPayStatus,
} from 'features/performanceBasedPay';
import { useFieldDataAndAccessor } from 'utils/useFieldDataAndAccessor';
import LeoSelectFieldMulti from 'components/input-fields/leoSelectField/LeoSelectFieldMulti';
import DialogFooterButtons from 'components/dialog/DialogFooterButtons';
import { PerformanceBasedPayProgressInfo } from './PerformanceBasedPayProgressInfo';

const styles = {
  radioGroup: {
    '.MuiFormLabel-root': {
      mb: 1,
      color: 'black',
    },
    '.MuiFormLabel-root.Mui-focused': {
      color: 'black',
    },
  },
};

interface PerformancePayFormValues extends Omit<NewPerformacePayModel, 'startDate' | 'endDate'> {
  date: {
    from?: string;
    to?: string;
  };
}

const NewPerformancePayDialog: FC<BaseDialogProps> = ({ open, onClose, ...props }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const formikRef = useRef<FormikProps<PerformancePayFormValues> | null>(null);
  const { data: workersData } = useWorkerListSearch({ withDeleted: false });
  const { data: workerIds, accessor: workerAccessor } = useFieldDataAndAccessor(
    workersData,
    'id',
    'name',
    true,
    'sapId'
  );

  const { data: activeJob, isFetching: isActiveJobFetching } =
    useGetActivePerformanceBasedPayJob(open);

  const { data: defaultInterval } = useDefaultPerformanceBasedPayInterval(open);
  const maxIntervalDays = defaultInterval?.maxIntervalDays;

  const {
    mutateAsync: createNewPerformancePay,
    isLoading,
    data: createResult,
    reset: resetCreateData,
  } = useCreatePerformanceBasedPay();
  const {
    data: pollData,
    remove: removeStatusData,
    isFetching: isJobStatusPolling,
  } = usePollPerformanceBasedPayStatus(createResult?.jobId || activeJob?.job?.id, open);

  const initialValues: FormikConfig<PerformancePayFormValues>['initialValues'] = {
    workerIds: [],
    allActiveWorkers: 'every',
    date: {
      from: defaultInterval?.startDate,
      to: defaultInterval?.endDate,
    },
  };

  const validationSchema: FormikConfig<PerformancePayFormValues>['validationSchema'] =
    Yup.object().shape({
      allActiveWorkers: Yup.string().nullable().required(),
      workerIds: Yup.mixed().when('allActiveWorkers', {
        is: (value: string) => value === 'selected',
        then: Yup.array().min(1),
        otherwise: Yup.mixed().nullable(),
      }),
      date: Yup.object()
        .shape({
          from: Yup.date().nullable().required(),
          to: Yup.date().nullable().required(),
        })
        .test('valid-interval', 'VALIDATION.INVALID_INTERVAL', ({ from, to }) => {
          if (!from || !to) return true;

          const { days } = DateTime.fromJSDate(to)
            .startOf('day')
            .diff(DateTime.fromJSDate(from).startOf('day'), 'days');

          return days >= 0 && (!maxIntervalDays || days <= maxIntervalDays);
        }),
    });

  const handleSubmit = async (data: PerformancePayFormValues) => {
    if (formikRef.current?.values && !isLoading) {
      const { date, ...rest } = data;
      await createNewPerformancePay({ ...rest, startDate: date?.from, endDate: date?.to });
    }
  };

  const handleClose = () => {
    formikRef.current?.resetForm();
    if (job?.status !== 'active') {
      resetCreateData();
      removeStatusData();
    }
    onClose();
  };

  useEffect(() => {
    if (pollData?.status === 'completed') {
      if (pollData.data?.errorCount) {
        showNotification({
          content: t('COMMON.OPERATION_UNSUCCESSFUL'),
          type: NotificationType.ERROR,
        });
      } else {
        showNotification({
          content: t('COMMON.OPERATION_SUCCESSFUL'),
          type: NotificationType.SUCCESS,
        });
      }

      queryClient.invalidateQueries({ queryKey: performanceBasedPayKeys.lists() });
    }
  }, [pollData, queryClient, t]);

  useEffect(() => {
    if (pollData?.status === 'completed' && !open) {
      resetCreateData();
      removeStatusData();
    }
  }, [open, pollData?.status, removeStatusData, resetCreateData]);

  const job = pollData ?? activeJob?.job;

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
      validationSchema={validationSchema}
    >
      {({ values, setFieldValue, errors, touched }) => {
        const onRadioGroupChange = (_e: ChangeEvent<HTMLInputElement>, value: string) => {
          setFieldValue('workerIds', []);
          setFieldValue('allActiveWorkers', value);
        };

        return (
          <Dialog
            title={<Trans i18nKey="NEW_PERFORMANCE_PAY_DIALOG.TITLE" />}
            component={Form}
            open={open}
            onClose={handleClose}
            actions={
              <DialogFooterButtons
                cancelClick={handleClose}
                cancelText={job?.status ? 'COMMON.CLOSE' : undefined}
                submitText="NEW_PERFORMANCE_PAY_DIALOG.SUBMIT"
                isLoading={isLoading || isJobStatusPolling}
                disableDialogButtons={Boolean(job?.status) || isActiveJobFetching}
              />
            }
            {...props}
          >
            <PerformanceBasedPayProgressInfo isActiveJobFetching={isActiveJobFetching} job={job} />

            {!isActiveJobFetching && !job?.status ? (
              <Grid container mt={2}>
                <Grid item xs={12} sx={styles.radioGroup}>
                  <Field
                    component={RadioGroup}
                    name="allActiveWorkers"
                    label={<Trans i18nKey="NEW_PERFORMANCE_PAY_DIALOG.RADIO_GROUP_QUESTION" />}
                    value={values.allActiveWorkers}
                    onChange={(e: ChangeEvent<HTMLInputElement>, v: string) =>
                      onRadioGroupChange(e, v)
                    }
                  >
                    <FormControlLabel
                      value="every"
                      control={<Radio />}
                      label={<Trans i18nKey="NEW_PERFORMANCE_PAY_DIALOG.FOR_EVERYONE" />}
                    />
                    <FormControlLabel
                      value="selected"
                      control={<Radio />}
                      label={<Trans i18nKey="NEW_PERFORMANCE_PAY_DIALOG.FOR_SELECTED" />}
                    />
                    {touched.allActiveWorkers && errors.allActiveWorkers && (
                      <FormHelperText error role="error">
                        {errors.allActiveWorkers}
                      </FormHelperText>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} mx={4}>
                  <LeoSelectFieldMulti
                    disabled={values.allActiveWorkers !== 'selected'}
                    name="workerIds"
                    data={workerIds}
                    accessor={workerAccessor}
                    readOnly
                    label="MUNKAVEGZOK.WORKER"
                  />
                </Grid>
                <Grid item xs={12} mb={2}>
                  <Divider />
                </Grid>
                <Grid item xs={12} mb={2}>
                  <LeoDateInterval
                    name="date"
                    isRelativeDateDisabled
                    maxIntervalDays={maxIntervalDays}
                    label={<Trans i18nKey="KOZOS_TELJESITMENYBEREK.PERIOD" />}
                  />
                </Grid>
              </Grid>
            ) : null}
          </Dialog>
        );
      }}
    </Formik>
  );
};

export default NewPerformancePayDialog;
